import { initialize, LDClient, LDFlagChangeset, LDOptions } from 'launchdarkly-js-client-sdk';
import React from 'react';

import {
  FeatureFlagsContext,
  FeatureFlagsContextType,
  FeatureFlagsFlagValue,
  FeatureFlagsUserContext,
} from '@/context/featureFlagsContext';
import { MockLDClient } from '@/mocks/mockLaunchdarklyClient';

interface Props {
  readonly userContext?: FeatureFlagsUserContext;
}

const cache = new Map<string, FeatureFlagsFlagValue>();

export const FeatureFlagsProvider: React.FC<React.PropsWithChildren<Props>> = (
  props: React.PropsWithChildren<Props>,
): React.ReactElement => {
  const { children, userContext } = props;
  const [cacheVersion, setCacheVersion] = React.useState<number>(0);
  const [client, setClient] = React.useState<LDClient | null>(null);

  const createClient = React.useCallback((): LDClient | null => {
    const {
      VITE_LAUNCHDARKLY_MOCK: LAUNCHDARKLY_MOCK,
      VITE_LAUNCHDARKLY_CLIENT_ID: LAUNCHDARKLY_CLIENT_ID,
    } = import.meta.env;

    const userKey = userContext?.key;
    if (!userKey) {
      // This happens temporarily and only briefly when the application
      // is loading.
      return null;
    }

    if (LAUNCHDARKLY_MOCK === 'true') {
      console.info('LaunchDarkly mock enabled. Feature flags will use local values.');
      return new MockLDClient();
    }

    const context = {
      kind: 'user',
      key: userKey ?? 'anonymous',
      anonymous: !userKey,
      ...userContext,
    };

    if (!LAUNCHDARKLY_CLIENT_ID) {
      console.warn('LaunchDarkly client ID not found. Feature flags will use default values.');
      return new MockLDClient();
    }

    const silent = {
      debug: (): void => {},
      info: (): void => {},
      warn: (): void => {},
      error: (): void => {},
    };

    const options: LDOptions = {
      logger: silent,
      bootstrap: 'localStorage',
    };

    return initialize(LAUNCHDARKLY_CLIENT_ID, context, options);
  }, [userContext]);

  React.useEffect((): (() => void) | undefined => {
    const ldClient = createClient();

    if (ldClient) {
      ldClient.on('change', (changes: LDFlagChangeset): void => {
        Object.entries(changes).forEach(([key]: [string, unknown]): void => {
          cache.delete(key);
          setCacheVersion((cacheVersion: number): number => cacheVersion + 1);
        });
      });

      ldClient.on('initialized', (): void => {
        cache.clear();
        setCacheVersion((cacheVersion: number): number => cacheVersion + 1);
      });

      void ldClient
        .waitForInitialization(1)
        .catch((error: unknown): void => {
          console.error('Failed to initialize LaunchDarkly:', error);
        })
        .finally((): void => {
          setClient(ldClient);
        });
    }

    return (): void => {
      if (ldClient) {
        void ldClient.close();
      }
    };
  }, [createClient, userContext]);

  const getFlag = React.useCallback(
    (flagKey: string, defaultValue: FeatureFlagsFlagValue): FeatureFlagsFlagValue => {
      if (!client) {
        return defaultValue;
      } else if (cache.has(flagKey)) {
        return cache.get(flagKey) ?? defaultValue;
      } else {
        const value = client.variation(flagKey, defaultValue);
        cache.set(flagKey, value);

        return value;
      }
    },
    [client],
  );

  const value = React.useMemo((): FeatureFlagsContextType => {
    // Funny, isn't it?
    void cacheVersion;

    return { getFlag: getFlag };
  }, [getFlag, cacheVersion]);

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
};
